import type { DocumentBlock } from "@magic-md/core";
import { useRef } from "react";
import { areDocumentBlocksRenderEqual } from "./document-block-equality";

export function useStableSemanticValue<T>(value: T): T {
    const ref = useRef<{ input: T; fingerprint: string; value: T } | null>(null);
    if (ref.current && ref.current.input === value) return ref.current.value;

    const fingerprint = semanticFingerprint(value);
    if (!ref.current || ref.current.fingerprint !== fingerprint) {
        ref.current = { input: value, fingerprint, value };
    } else {
        ref.current.input = value;
    }
    return ref.current.value;
}

export function useStableDocumentBlocks<T extends DocumentBlock[] | null | undefined>(
    blocks: T,
): T {
    const ref = useRef<DocumentBlock[] | null | undefined>(blocks);
    if (!blocks) {
        ref.current = blocks;
        return blocks;
    }

    const previous = ref.current;
    if (!previous) {
        ref.current = blocks;
        return blocks;
    }

    let changed = previous.length !== blocks.length;
    const next = blocks.map((block, index) => {
        const previousBlock = previous[index];
        if (
            previousBlock &&
            areDocumentBlocksRenderEqual(previousBlock, block)
        ) {
            return previousBlock;
        }
        changed = true;
        return block;
    });

    if (!changed) return previous as T;
    ref.current = next;
    return next as T;
}

function semanticFingerprint(value: unknown): string {
    return JSON.stringify(normalizeSemanticValue(value));
}

function normalizeSemanticValue(value: unknown): unknown {
    if (value == null || typeof value !== "object") return value;

    if (value instanceof Map) {
        return {
            __type: "Map",
            entries: [...value.entries()]
                .sort(([left], [right]) => String(left).localeCompare(String(right)))
                .map(([key, entryValue]) => [key, normalizeSemanticValue(entryValue)]),
        };
    }

    if (value instanceof Set) {
        return {
            __type: "Set",
            values: [...value.values()]
                .map(normalizeSemanticValue)
                .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
        };
    }

    if (Array.isArray(value)) {
        return value.map(normalizeSemanticValue);
    }

    const object = value as Record<string, unknown>;
    const normalized: Record<string, unknown> = {};
    for (const key of Object.keys(object).sort()) {
        const entryValue = object[key];
        if (isSourceSpanKey(key, entryValue) || isRangeKey(key, entryValue)) {
            continue;
        }
        normalized[key] = normalizeSemanticValue(entryValue);
    }
    return normalized;
}

function isSourceSpanKey(key: string, value: unknown): boolean {
    if (key !== "source" || value == null || typeof value !== "object") {
        return false;
    }
    const source = value as Record<string, unknown>;
    return (
        typeof source.startOffset === "number" &&
        typeof source.endOffset === "number"
    );
}

function isRangeKey(key: string, value: unknown): boolean {
    if (key !== "range" || value == null || typeof value !== "object") {
        return false;
    }
    const range = value as Record<string, unknown>;
    return typeof range.start === "number" && typeof range.end === "number";
}
